<template>
  <div class="dashboard">
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Escritorio</h1>
        <p class="content-header-subtitle">Bienvenido al portal de gestión de proyectos Gescom</p>
      </div>
      <div class="content-header-right">
        <b-button variant="primary" @click="refreshData">
          <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
          Actualizar
        </b-button>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="loading" class="text-center mb-4">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2">Cargando datos del dashboard...</p>
    </div>

    <!-- Statistics Cards -->
    <div class="row" v-if="!loading">
      <div class="col-xl-3 col-md-6 col-12">
        <b-card class="card-statistics clickable-card" @click="goToProyectos">
          <b-card-body>
            <div class="d-flex align-items-center">
              <div class="avatar bg-light-primary p-50">
                <feather-icon icon="FolderIcon" size="24" class="text-primary" />
              </div>
              <div class="ml-1">
                <h4 class="mb-0">{{ statistics.proyectosActivos }}</h4>
                <p class="card-text font-small-3 mb-0">Proyectos Activos</p>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>
      <div class="col-xl-3 col-md-6 col-12">
        <b-card class="card-statistics clickable-card" @click="goToActividades">
          <b-card-body>
            <div class="d-flex align-items-center">
              <div class="avatar bg-light-success p-50">
                <feather-icon icon="CheckSquareIcon" size="24" class="text-success" />
              </div>
              <div class="ml-1">
                <h4 class="mb-0">{{ statistics.actividadesCompletadas }}</h4>
                <p class="card-text font-small-3 mb-0">Actividades Completadas</p>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>
      <div class="col-xl-3 col-md-6 col-12">
        <b-card class="card-statistics clickable-card" @click="goToObras">
          <b-card-body>
            <div class="d-flex align-items-center">
              <div class="avatar bg-light-info p-50">
                <feather-icon icon="BuildingIcon" size="24" class="text-info" />
              </div>
              <div class="ml-1">
                <h4 class="mb-0">{{ statistics.obrasEnProgreso }}</h4>
                <p class="card-text font-small-3 mb-0">Obras en Progreso</p>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>
      <div class="col-xl-3 col-md-6 col-12">
        <b-card class="card-statistics clickable-card" @click="goToUsuarios">
          <b-card-body>
            <div class="d-flex align-items-center">
              <div class="avatar bg-light-warning p-50">
                <feather-icon icon="UsersIcon" size="24" class="text-warning" />
              </div>
              <div class="ml-1">
                <h4 class="mb-0">{{ statistics.totalUsuarios }}</h4>
                <p class="card-text font-small-3 mb-0">Usuarios Activos</p>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>

    <!-- Charts and Tables -->
    <div class="row" v-if="!loading">
      <!-- Proyectos Recientes -->
      <div class="col-lg-8 col-12">
        <b-card title="Proyectos Recientes" class="mb-0">
          <b-table
            :items="proyectosRecientes"
            :fields="proyectosFields"
            responsive
            striped
            hover
          >
            <template #cell(estado)="data">
              <b-badge
                :variant="getEstadoVariant(data.item.estado)"
                class="text-capitalize"
              >
                {{ data.item.estado }}
              </b-badge>
            </template>
            <template #cell(fechaInicio)="data">
              {{ formatearFecha(data.item.fechaInicio) }}
            </template>
            <template #cell(acciones)="data">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="verProyecto(data.item.id)"
              >
                Ver
              </b-button>
            </template>
          </b-table>
        </b-card>
      </div>

      <!-- Actividades Pendientes -->
      <div class="col-lg-4 col-12">
        <b-card title="Actividades Pendientes" class="mb-0">
          <div class="timeline">
            <div
              v-for="actividad in actividadesPendientes"
              :key="actividad.id"
              class="timeline-item"
            >
              <div class="timeline-marker bg-primary"></div>
              <router-link
                class="timeline-content d-block"
                :to="{ name: 'actividades-detalle', params: { id: actividad.id } }"
              >
                <h6 class="timeline-title">{{ actividad.titulo }}</h6>
                <p class="timeline-text">{{ actividad.descripcion }}</p>
                <small class="text-muted d-block">
                  Próxima acción: {{ formatearFecha(actividad.proximaAccionFecha || actividad.fechaVencimiento || actividad.fecha) }}
                </small>
              </router-link>
            </div>
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import { dashboardService } from '@/services/dashboardService'
import { formatearFecha as formatearFechaUtil } from '@/utils/dateUtils'

export default {
  name: 'Dashboard',
  data() {
    return {
      statistics: {
        totalProyectos: 0,
        totalActividades: 0,
        totalObras: 0,
        totalUsuarios: 0,
        proyectosActivos: 0,
        actividadesCompletadas: 0,
        obrasEnProgreso: 0,
      },
      proyectosFields: [
        { key: 'nombre', label: 'Nombre' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'estado', label: 'Estado' },
        { key: 'fechaInicio', label: 'Fecha Inicio' },
        { key: 'acciones', label: 'Acciones' },
      ],
      proyectosRecientes: [],
      actividadesPendientes: [],
      loading: false,
    }
  },
  async mounted() {
    await this.loadDashboardData()
  },
  methods: {
    async loadDashboardData() {
      this.loading = true
      try {
        const data = await dashboardService.getStatistics()
        this.statistics = {
          totalProyectos: data.totalProyectos,
          totalActividades: data.totalActividades,
          totalObras: data.totalObras,
          totalUsuarios: data.totalUsuarios,
          proyectosActivos: data.proyectosActivos,
          actividadesCompletadas: data.actividadesCompletadas,
          obrasEnProgreso: data.obrasEnProgreso,
        }
        this.proyectosRecientes = data.proyectosRecientes
        this.actividadesPendientes = data.actividadesPendientes
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error)
        this.$toast({
          component: {
            template: '<div>Error al cargar los datos del dashboard</div>',
          },
          props: {
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async refreshData() {
      await this.loadDashboardData()
      this.$toast({
        component: {
          template: '<div>Datos actualizados correctamente</div>',
        },
        props: {
          variant: 'success',
        },
      })
    },
    getEstadoVariant(estado) {
      const variants = {
        'En Progreso': 'primary',
        'Completado': 'success',
        'Pendiente': 'warning',
        'Cancelado': 'danger',
      }
      return variants[estado] || 'secondary'
    },
    formatearFecha(fecha) {
      const formateada = formatearFechaUtil(fecha)
      return formateada || (fecha || '')
    },
    verProyecto(id) {
      this.$router.push({ name: 'proyectos-detalle', params: { id } })
    },
    goToProyectos() {
      this.$router.push({ name: 'proyectos-lista' })
    },
    goToActividades() {
      this.$router.push({ name: 'actividades-lista' })
    },
    goToObras() {
      this.$router.push({ name: 'obras-lista' })
    },
    goToUsuarios() {
      this.$router.push({ name: 'usuarios-lista' })
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.card-statistics {
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card-statistics:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.clickable-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.clickable-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  border-color: #007bff;
}

.clickable-card:active {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.avatar {
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline-item {
  position: relative;
  margin-bottom: 1.5rem;
}

.timeline-marker {
  position: absolute;
  left: -2rem;
  top: 0.5rem;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.timeline-content {
  padding-left: 1rem;
}

.timeline-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #2c3e50;
}

.timeline-text {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .content-header-right {
    margin-top: 1rem;
  }
}
</style>
